import type { CoachResponse, QuoteResponsePagedData } from '@on3/api';
import { type IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import type { AxiosError } from 'axios';
import { CoachQuotes } from 'components/Db/Profile/Coach/CoachQuotes';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';

export interface ICoachQuotesPageProps {
  coachQuotes: QuoteResponsePagedData;
  coachInfo: CoachResponse;
}

interface ICoachQuotesPage extends ICoachQuotesPageProps, IGenericPageProps {}

const CoachQuotesPage = (props: ICoachQuotesPage) => {
  const { coachQuotes, coachInfo } = props;

  const title = `${coachInfo?.fullName} Quotes | On3.com`;
  const description = `Popular quotes from ${coachInfo?.fullName}`;
  const noIndex = coachQuotes?.pagination?.count === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <DatabaseLayout>
        <CoachQuotes {...props} />
      </DatabaseLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { id } = ctx.query;
  const coachId = id?.toString().split('-').pop();

  try {
    const { page = 1 } = ctx.query;
    const params = {
      page,
    };
    const [coachInfo, coachQuotes] = await Promise.allSettled([
      proxyApi
        .get<CoachResponse>(`/public/rdb/v1/coaches/${coachId}/profile`)
        .then((r) => r.data),
      proxyApi
        .get<QuoteResponsePagedData>(
          `/public/rdb/v1/quotes?personKey=${coachId}`,
          { params },
        )
        .then((r) => r.data),
    ]);

    const data = {
      pageType: 'database',
      siteData: siteData || [],
      userData: userData || {},
      coachInfo: coachInfo.status === 'fulfilled' ? coachInfo.value : [],
      coachQuotes: coachQuotes.status === 'fulfilled' ? coachQuotes.value : [],
    };

    return { props: { ...data } };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.log(err.message);

    return { props: {} };
  }
};

export default withValidUrl(CoachQuotesPage);
